import Image from 'next/image'
import { cn } from './cn'
import { ImageDistante } from './ImageDistante'

/**
 * L'image de couverture d'un module, quelle que soit la forme de son adresse.
 *
 * ## ⚠️ La règle que ce composant existe pour ne plus réécrire
 *
 * Une couverture a **deux formes possibles**, et elles ne se rendent pas de la
 * même façon :
 *
 *   · un **chemin du dépôt** — `/brand/covers/capsules/krea.jpg` — pour les 31
 *     modules repris du catalogue. Il passe par `next/image`, qui en produit une
 *     version à la taille exacte : mesuré le 2026-08-28, **1 010 octets en WebP
 *     à 96 px de large, contre 46 379 octets pour le JPEG d'origine**. Un
 *     facteur 46.
 *   · une **adresse distante** — `https://drive.google.com/thumbnail?id=…` —
 *     pour les modules déposés. Elle doit passer par une balise `<img>` nue :
 *     `next/image` sur un hôte non déclaré répond `400 — "url" parameter is not
 *     allowed`, mesuré en production le même jour.
 *
 * Cette règle était réécrite à trois endroits — la grille de l'apprenant, la
 * liste du formateur, le fond du lecteur — et les trois ne la disaient pas
 * pareil : la liste du formateur ne rendait **que** les adresses `https`, si
 * bien que les 31 couvertures du catalogue n'y montraient qu'un aplat violet.
 * Un composant, une règle.
 *
 * ## Le clignotement, et ce qui le causait vraiment
 *
 * Baptiste, le 2026-08-28 : « je retrouve parfois des microsecondes de
 * chargement avant aperçu avec un fond violet ». Le coupable n'était pas le
 * poids mais **`cache-control: public, max-age=0`**, que Next pose par défaut
 * sur `public/` : le navigateur revalidait l'image à chaque affichage. Corrigé
 * dans `next.config.ts`. Le passage par l'optimiseur, lui, s'attaque au poids —
 * les deux comptent, mais c'est la revalidation qui faisait clignoter.
 *
 * ⚠️ **L'aplat de fond reste sous l'image**, et il le faut : c'est lui qui tient
 * la place quand aucune couverture n'existe. Il est simplement moins souvent
 * visible.
 */
export function Couverture({
  url,
  sizes,
  priorite = false,
  className,
}: {
  url: string | null
  /** Les largeurs réellement demandées, pour que l'optimiseur ne serve pas trop grand. */
  sizes: string
  /** Vrai pour les images visibles d'emblée : elles ne doivent pas être différées. */
  priorite?: boolean
  className?: string
}) {
  if (!url) return null

  if (url.startsWith('/') && !url.startsWith('//')) {
    return (
      <Image
        src={url}
        alt=""
        fill
        sizes={sizes}
        priority={priorite}
        className={cn('object-cover', className)}
      />
    )
  }

  if (!url.startsWith('https://')) return null

  /*
   * ⚠️ **Une adresse distante peut échouer, et ça se voyait.** Drive limite les
   * vignettes qu'il sert : sur un réseau mobile, l'image manque parfois et le
   * navigateur dessine alors son icône de rupture par-dessus l'aplat de marque —
   * signalé par Baptiste le 2026-09-02, capture à l'appui, sur une couverture
   * dont l'adresse était pourtant valide (200, JPEG 760×428, vérifié le même
   * jour). `ImageDistante` s'efface au lieu de se casser ; l'aplat reprend la
   * place, ce qu'il fait déjà quand aucune couverture n'existe.
   */
  return <ImageDistante url={url} priorite={priorite} className={className} />
}
